import React from 'react';
import type { ScannerResult, ScannerColumn, ColumnFormat, ScannerSortConfig } from '@/services/scannerService';

// ── Value extraction ────────────────────────────
// Maps a ScannerColumn.field to the corresponding ScannerResult property value.
function getFieldValue(result: ScannerResult, field: string): any {
  // camelCase the field name to match TS property names
  const key = field.charAt(0).toLowerCase() + field.slice(1);
  return (result as any)[key];
}

// ── Formatting ──────────────────────────────────
function formatValue(value: any, format: ColumnFormat, field: string): string {
  if (value == null) return '-';

  switch (format) {
    case 'Currency':
      return (typeof value === 'number' ? value : Number(value)).toFixed(2);

    case 'Percent':
    case 'ChangePercent': {
      const n = typeof value === 'number' ? value : Number(value);
      const sign = n >= 0 ? '+' : '';
      return `${sign}${n.toFixed(2)}%`;
    }

    case 'Change': {
      const n = typeof value === 'number' ? value : Number(value);
      const sign = n >= 0 ? '+' : '';
      return `${sign}${n.toFixed(2)}`;
    }

    case 'Volume': {
      const n = typeof value === 'number' ? value : Number(value);
      if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
      if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
      if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
      return n.toString();
    }

    case 'Number': {
      const n = typeof value === 'number' ? value : Number(value);
      return n.toFixed(2);
    }

    case 'Time': {
      if (!value) return '-';
      const date = new Date(value);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        timeZone: 'America/New_York',
      });
    }

    case 'DateTime': {
      if (!value) return '-';
      const date = new Date(value);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleString('en-US', {
        month: 'numeric', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
        timeZone: 'America/New_York',
      });
    }

    case 'Text':
    default:
      return String(value);
  }
}

// ── Color logic ─────────────────────────────────
function getValueColor(value: any, format: ColumnFormat, field: string): string {
  if (format === 'ChangePercent' || format === 'Change' || format === 'Percent') {
    const n = typeof value === 'number' ? value : Number(value);
    if (n > 0) return 'text-emerald-500';
    if (n < 0) return 'text-red-400';
    return 'text-muted-foreground';
  }

  // Number fields with directional meaning
  if (format === 'Number') {
    const directional = ['zenV', 'zenP', 'atrVwap'];
    const key = field.charAt(0).toLowerCase() + field.slice(1);
    if (directional.includes(key)) {
      const n = typeof value === 'number' ? value : Number(value);
      if (n > 0) return 'text-emerald-500';
      if (n < 0) return 'text-red-400';
      return 'text-muted-foreground';
    }
    // AtrSpd gets a highlight color
    if (key === 'atrSpd') return 'text-yellow-500';
    // AtrHoD negative color logic (lower = closer to HOD = better)
    if (key === 'atrHoD') {
      const n = typeof value === 'number' ? value : Number(value);
      if (n > 0) return 'text-emerald-500';
      return 'text-muted-foreground';
    }
  }

  if (format === 'Currency') {
    // Color price by related changePercent if available — but we don't have context here.
    // Return default
    return '';
  }

  if (format === 'Volume') return 'text-muted-foreground';

  return '';
}

// ── Row background based on change ──────────────
function getRowBgColor(changePercent: number): string {
  if (changePercent > 5) return 'bg-green-600/30';
  if (changePercent > 2) return 'bg-green-500/20';
  if (changePercent > 0) return 'bg-green-400/10';
  if (changePercent < -5) return 'bg-red-600/30';
  if (changePercent < -2) return 'bg-red-500/20';
  if (changePercent < 0) return 'bg-red-400/10';
  return '';
}

// ── Default columns fallback ────────────────────
const DEFAULT_COLUMNS: ScannerColumn[] = [
  { field: 'Symbol', header: 'Name', width: 60, format: 'Text', visible: true, order: 0 },
  { field: 'Price', header: 'Price', width: 60, format: 'Currency', visible: true, order: 1 },
  { field: 'ChangePercent', header: 'Chg%', width: 60, format: 'ChangePercent', visible: true, order: 2 },
  { field: 'Volume', header: 'Vol', width: 70, format: 'Volume', visible: true, order: 3 },
  { field: 'Float', header: 'Float', width: 70, format: 'Volume', visible: true, order: 4 },
  { field: 'ChangePercent2Min', header: '2Min%', width: 60, format: 'ChangePercent', visible: true, order: 5 },
];

// ── Props ───────────────────────────────────────
export interface DynamicScannerTableProps {
  results: ScannerResult[];
  columns?: ScannerColumn[];
  sort?: ScannerSortConfig;
  onSymbolClick?: (symbol: string) => void;
}

/**
 * Dynamic scanner table that renders columns based on backend-defined column configuration.
 * Falls back to DEFAULT_COLUMNS if none are provided.
 */
const DynamicScannerTable: React.FC<DynamicScannerTableProps> = ({
  results,
  columns,
  sort,
  onSymbolClick,
}) => {
  // Use provided columns or fallback; filter visible, sort by order
  const visibleColumns = (columns && columns.length > 0 ? columns : DEFAULT_COLUMNS)
    .filter(c => c.visible !== false)
    .sort((a, b) => a.order - b.order);

  const colCount = visibleColumns.length;

  return (
    <div style={{ minWidth: `${Math.max(colCount * 65, 500)}px` }}>
      {/* Column Headers */}
      <div
        className="grid gap-1 px-2 py-1 text-xs font-medium text-muted-foreground border-b border-border/30 bg-muted/30 sticky top-0 z-10"
        style={{ gridTemplateColumns: visibleColumns.map(c => c.field === 'Symbol' ? 'minmax(50px, 1fr)' : `${c.width}px`).join(' ') }}
      >
        {visibleColumns.map((col) => (
          <div
            key={`${col.field}-${col.order}`}
            className={col.field === 'Symbol' ? 'text-left' : 'text-right'}
            title={sort?.field === col.field ? `Sorted ${sort.descending ? '↓' : '↑'}` : undefined}
          >
            {col.header}
            {sort?.field === col.field && (
              <span className="ml-0.5 text-primary text-[9px]">{sort.descending ? '▼' : '▲'}</span>
            )}
          </div>
        ))}
      </div>

      {/* Results */}
      <div>
        {results.length === 0 ? (
          <div className="flex items-center justify-center h-20 text-sm text-muted-foreground">
            No results
          </div>
        ) : (
          results.map((result, index) => (
            <div
              key={result.symbol}
              onClick={() => onSymbolClick?.(result.symbol)}
              className={`grid gap-1 px-2 py-1 text-xs cursor-pointer hover:bg-accent/50 transition-colors ${
                index % 2 === 0 ? 'bg-background/30' : ''
              } ${getRowBgColor(result.changePercent)}`}
              style={{ gridTemplateColumns: visibleColumns.map(c => c.field === 'Symbol' ? 'minmax(50px, 1fr)' : `${c.width}px`).join(' ') }}
            >
              {visibleColumns.map((col) => {
                const value = getFieldValue(result, col.field);
                const formatted = formatValue(value, col.format, col.field);
                const colorClass = getValueColor(value, col.format, col.field);

                if (col.field === 'Symbol') {
                  return (
                    <div key={`${col.field}-${col.order}`} className="font-medium text-foreground truncate" title={result.name}>
                      {result.symbol}
                    </div>
                  );
                }

                // For Volume field showing zero or negative Float — show dash
                if (col.field === 'Float' && (value <= 0 || !value)) {
                  return (
                    <div key={`${col.field}-${col.order}`} className="text-right text-muted-foreground">-</div>
                  );
                }

                return (
                  <div
                    key={`${col.field}-${col.order}`}
                    className={`text-right ${colorClass} ${
                      (col.format === 'ChangePercent' || col.format === 'Change') ? 'font-medium' : ''
                    }`}
                  >
                    {formatted}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ── Compact variant for mini panels ─────────────
// Shows first 3-4 columns only in a compact table format.
export interface MiniScannerTableProps {
  results: ScannerResult[];
  columns?: ScannerColumn[];
  maxRows?: number;
  onSymbolClick?: (symbol: string) => void;
}

const MINI_FALLBACK_COLUMNS: ScannerColumn[] = [
  { field: 'Symbol', header: 'Sym', width: 50, format: 'Text', visible: true, order: 0 },
  { field: 'Price', header: 'Price', width: 60, format: 'Currency', visible: true, order: 1 },
  { field: 'ChangePercent', header: 'Chg%', width: 55, format: 'ChangePercent', visible: true, order: 2 },
  { field: 'Volume', header: 'Vol', width: 60, format: 'Volume', visible: true, order: 3 },
];

export const MiniScannerTable: React.FC<MiniScannerTableProps> = ({
  results,
  columns,
  maxRows = 15,
  onSymbolClick,
}) => {
  // For mini panel: take first 4 visible columns, always include Symbol
  const allCols = (columns && columns.length > 0 ? columns : MINI_FALLBACK_COLUMNS)
    .filter(c => c.visible !== false)
    .sort((a, b) => a.order - b.order);

  // Take up to 4 columns for mini view
  const visibleColumns = allCols.slice(0, 4);

  return (
    <table className="w-full text-[11px]">
      <thead>
        <tr className="border-b border-border/30 text-muted-foreground">
          {visibleColumns.map((col) => (
            <th
              key={`${col.field}-${col.order}`}
              className={`${col.field === 'Symbol' ? 'text-left' : 'text-right'} px-2 py-1 font-medium`}
            >
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {results.slice(0, maxRows).map((r) => (
          <tr
            key={r.symbol}
            className="border-b border-border/10 hover:bg-muted/30 cursor-pointer transition-colors"
            onClick={() => onSymbolClick?.(r.symbol)}
          >
            {visibleColumns.map((col) => {
              const value = getFieldValue(r, col.field);

              if (col.field === 'Symbol') {
                return (
                  <td key={`${col.field}-${col.order}`} className="px-2 py-0.5 font-medium text-foreground">
                    {r.symbol}
                  </td>
                );
              }

              if (col.format === 'Currency') {
                return (
                  <td key={`${col.field}-${col.order}`} className="px-2 py-0.5 text-right font-mono">
                    ${formatValue(value, col.format, col.field)}
                  </td>
                );
              }

              const colorClass = getValueColor(value, col.format, col.field);
              return (
                <td key={`${col.field}-${col.order}`} className={`px-2 py-0.5 text-right font-mono ${colorClass}`}>
                  {formatValue(value, col.format, col.field)}
                </td>
              );
            })}
          </tr>
        ))}
        {results.length === 0 && (
          <tr>
            <td colSpan={visibleColumns.length} className="py-4 text-center text-muted-foreground text-xs">
              No results
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default DynamicScannerTable;
